<template>
    <div class="page-wrapper" v-if="totalPages > 1">
        <!-- Previous Page Button -->
        <button 
            class="pagination-nav-button" 
            :class="{ 'disabled': page <= 1 }" 
            @click="changePage(page - 1)"
            title="Previous Page"
        >
            <i class="material-icons">chevron_left</i>
        </button>
        
        <PaginatorItem
            v-for="(item, index) in visiblePages"
            :key="index"
            :page="item"
            :lastPage="totalPages"
            :active="page === item"
            @change="$emit('change', $event)"
        />
        
        <!-- Next Page Button -->
        <button 
            class="pagination-nav-button" 
            :class="{ 'disabled': page >= totalPages }" 
            @click="changePage(page + 1)"
            title="Next Page"
        >
            <i class="material-icons">chevron_right</i>
        </button>
    </div>
</template>

<script>
import PaginatorItem from "./PaginatorItem.vue"

export default {
    emits: ["change"],
    components: { PaginatorItem },
    props: {
        totalPages: { type: Number, default: () => 0 },
        page: { type: Number, default: () => 1 },
        maxPage: { type: Number, default: () => 10 }, // Maximum visible page numbers
    },
    mounted() {
        if (!isNaN(Number(this.$route.query.page))) {
            const queriedPage = Number(this.$route.query.page)
            if (queriedPage >= 1 && queriedPage <= this.totalPages) {
                this.$emit("change", queriedPage)
                return
            }
        }
        this.$emit("change", this.page)
    },
    methods: {
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.$emit("change", page)
            }
        }
    },
    computed: {
        visiblePages() {
            const totalPages = this.totalPages
            const maxPage = this.maxPage
            const currentPage = this.page

            // If totalPages is less than or equal to maxPage, show all pages
            if (totalPages <= maxPage) {
                return Array.from({ length: totalPages }, (_, i) => i + 1)
            }

            const half = Math.floor(maxPage / 2)
            let startPage, endPage

            // Determine startPage and endPage based on currentPage
            if (currentPage <= half) {
                startPage = 1
                endPage = maxPage
            } else if (currentPage + half >= totalPages) {
                startPage = totalPages - maxPage + 1
                endPage = totalPages
            } else {
                startPage = currentPage - half
                endPage = currentPage + half
            }

            // Add ellipses if necessary
            const pages = []
            if (startPage > 1) {
                pages.push(1)
                if (startPage > 2) {
                    pages.push("...")
                }
            }
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i)
            }
            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pages.push("...")
                }
                pages.push(totalPages)
            }
            return pages
        },
    },
}
</script>

<style lang="scss">
@import "@/assets/variables.scss";

.page-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    justify-content: center;
    padding: 8px 0;
    
    .pagination-nav-button {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid $lightBorderColor;
        border-radius: 6px;
        background-color: $lightCardBg;
        cursor: pointer;
        transition: all 0.2s ease;
        
        i {
            font-size: 18px;
            color: $textColor;
        }
        
        &:hover:not(.disabled) {
            background-color: $lightHoverColor;
            border-color: $primaryColor;
        }
        
        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
    
    :deep(.dark) {
        .pagination-nav-button {
            background-color: $darkCardBg;
            border-color: $darkBorderColor;
            
            i {
                color: $darkTextColor;
            }
            
            &:hover:not(.disabled) {
                background-color: rgba(255, 255, 255, 0.08);
                border-color: $secondaryColorDark;
            }
        }
    }
}
</style>
